import type { QueryClient } from '@tanstack/react-query'
import {
  applyFixtureModelEdit,
  applyItemEdit,
  applyLineCount,
  applyLocationEdit,
  newClientId,
  provisionalFixtureModel,
  provisionalItem,
  provisionalLocation,
  provisionalStocktake,
  QUEUED_PHOTO_MAX,
  type Changeset,
  type EquipmentItem,
  type EquipmentItemInput,
  type FieldEdits,
  type FieldValues,
  type FixtureModel,
  type FixtureModelInput,
  type Location,
  type LocationInput,
  type RecordImage,
  type RecordRef,
  type StocktakeDetail,
  type StocktakeLine,
  type StocktakeSummary,
  type SyncEntity,
} from '@mads/shared'
import { queryKeys } from './queries'
import { useQueueStore } from './queue'
import { localPhotoUrl, putQueuedPhoto, shrinkForQueue } from './photoStore'

/**
 * What a write does when there is no connection (issue #64, step 2): it is
 * queued, the caches are patched as if it had landed, and the caller is told it
 * worked — because from the walker's point of view it has.
 *
 * All of it lives here rather than in the screens, which is what the seam in
 * `queries.ts` is for: `Inventory.tsx` and `Stocktake.tsx` go on calling hooks
 * and never learn there is a queue.
 */

function changesetId(): string {
  return `cs_${crypto.randomUUID()}`
}

function now(): string {
  return new Date().toISOString()
}

/** A record is named by whichever id it has — a real one, or the queue's. */
function refFor(id: number, clientId?: string): RecordRef {
  return clientId === undefined ? { id } : { clientId }
}

/** What `GET /api/stocktakes` answers, which is what the shell caches. */
type StocktakeList = { open: StocktakeSummary | null; history: StocktakeSummary[] }

async function enqueue(changeset: Changeset): Promise<void> {
  await useQueueStore.getState().add(changeset)
}

// — reading the base a field was edited from —

/**
 * What this device believed the field read before the edit — which is what the
 * server compares against, and the only thing that makes a conflict answerable.
 *
 * The optics are read **as stored, not as resolved**. `InventoryPresenter`
 * serves a lantern's effective optics so every screen reads a spec the same way
 * (issue #30), but an edit sets the item's own column and the form holds the
 * *override* (issue #59) — so a base taken from the resolved value would report
 * a conflict every time a linked lantern's inherited lamp differed from its
 * blank override, which is not a disagreement at all. `RecordFields` on the
 * server reads exactly the same way.
 */
function itemBase(item: EquipmentItem, field: string): unknown {
  switch (field) {
    case 'locationId':
      return item.location?.id ?? null
    case 'fixtureModelId':
      return item.fixtureModel?.id ?? null
    case 'lamp':
      return item.lampOverridden ? item.lamp : null
    case 'watts':
      return item.wattsOverridden ? item.watts : null
    case 'lightType':
    case 'gelFrame':
    case 'goboFrame':
    case 'barnDoor':
      // While a lantern is linked these columns are the model's and the item's
      // are empty, which is what the server holds too.
      return item.fixtureModel === null ? item[field] : null
    default:
      return (item as unknown as Record<string, unknown>)[field] ?? null
  }
}

function editsFor(
  fields: FieldValues,
  base: (field: string) => unknown,
): FieldEdits {
  const edits: Record<string, { base: unknown; value: unknown }> = {}

  for (const [field, value] of Object.entries(fields)) {
    edits[field] = { base: base(field), value }
  }

  return edits
}

/**
 * Only what the form actually carried. `undefined` means the field was not part
 * of this edit; `null` means clear it, which for a lamp is how a lantern goes
 * back to reading its model — so the two must not be flattened together.
 */
function carried(input: Record<string, unknown>): FieldValues {
  const out: Record<string, unknown> = {}

  for (const [field, value] of Object.entries(input)) {
    if (value !== undefined) out[field] = value
  }

  return out
}

// — patching the caches —

/**
 * The cached inventory. One entry, since issue #70 — the filters are applied on
 * the device, so there is no longer a list per filter combination to keep in
 * step. `api.items()` answers with the records themselves rather than a wrapper,
 * so that is what is cached.
 */
function patchItemLists(queryClient: QueryClient, update: (items: EquipmentItem[]) => EquipmentItem[]) {
  queryClient.setQueryData<EquipmentItem[]>(queryKeys.items, (previous) =>
    previous === undefined ? previous : update(previous),
  )
}

function patchLocations(queryClient: QueryClient, update: (locations: Location[]) => Location[]) {
  queryClient.setQueryData<Location[]>(queryKeys.locations, (previous) =>
    previous === undefined ? previous : update(previous),
  )
}

function patchFixtureModels(
  queryClient: QueryClient,
  update: (models: FixtureModel[]) => FixtureModel[],
) {
  queryClient.setQueryData<FixtureModel[]>(queryKeys.fixtureModels, (previous) =>
    previous === undefined ? previous : update(previous),
  )
}

// — items —

export async function queueItemCreate(
  queryClient: QueryClient,
  input: EquipmentItemInput,
): Promise<EquipmentItem> {
  const clientId = newClientId()
  const fields = carried(input as unknown as Record<string, unknown>)
  const locations = queryClient.getQueryData<Location[]>(queryKeys.locations) ?? []
  const record = provisionalItem(clientId, fields, locations)

  await enqueue({
    id: changesetId(),
    entity: 'item',
    kind: 'create',
    at: now(),
    clientId,
    fields,
  })

  patchItemLists(queryClient, (items) => [record, ...items])
  queryClient.setQueryData(queryKeys.item(record.id), record)

  return record
}

export async function queueItemUpdate(
  queryClient: QueryClient,
  id: number,
  input: Partial<EquipmentItemInput>,
): Promise<EquipmentItem> {
  const current = findItem(queryClient, id)

  if (current === null) {
    throw new Error('That item is not saved on this device, so it cannot be changed offline.')
  }

  const fields = carried(input as Record<string, unknown>)
  const record = applyItemEdit(current, fields)

  await enqueue({
    id: changesetId(),
    entity: 'item',
    kind: 'update',
    at: now(),
    target: refFor(id, current.clientId),
    fields: editsFor(fields, (field) => itemBase(current, field)),
  })

  patchItemLists(queryClient, (items) =>
    items.map((item) => (item.id === id ? record : item)),
  )
  queryClient.setQueryData(queryKeys.item(id), record)

  return record
}

export async function queueItemDelete(queryClient: QueryClient, id: number): Promise<void> {
  const current = findItem(queryClient, id)

  await enqueue({
    id: changesetId(),
    entity: 'item',
    kind: 'delete',
    at: now(),
    target: refFor(id, current?.clientId),
  })

  patchItemLists(queryClient, (items) => items.filter((item) => item.id !== id))
  queryClient.removeQueries({ queryKey: queryKeys.item(id) })
}

function findItem(queryClient: QueryClient, id: number): EquipmentItem | null {
  const direct = queryClient.getQueryData<EquipmentItem>(queryKeys.item(id))

  if (direct !== undefined) return direct

  return allCachedItems(queryClient).find((item) => item.id === id) ?? null
}

// — locations —

export async function queueLocationCreate(
  queryClient: QueryClient,
  input: LocationInput,
): Promise<Location> {
  const clientId = newClientId()
  const fields = carried(input as unknown as Record<string, unknown>)
  const record = provisionalLocation(clientId, fields)

  await enqueue({
    id: changesetId(),
    entity: 'location',
    kind: 'create',
    at: now(),
    clientId,
    fields,
  })

  patchLocations(queryClient, (locations) => [...locations, record])

  return record
}

export async function queueLocationUpdate(
  queryClient: QueryClient,
  id: number,
  input: Partial<LocationInput>,
): Promise<Location> {
  const locations = queryClient.getQueryData<Location[]>(queryKeys.locations) ?? []
  const current = locations.find((location) => location.id === id)

  if (current === undefined) {
    throw new Error('That place is not saved on this device, so it cannot be changed offline.')
  }

  const fields = carried(input as Record<string, unknown>)
  const record = applyLocationEdit(current, fields)

  await enqueue({
    id: changesetId(),
    entity: 'location',
    kind: 'update',
    at: now(),
    target: refFor(id, current.clientId),
    fields: editsFor(fields, (field) =>
      (current as unknown as Record<string, unknown>)[field] ?? null,
    ),
  })

  patchLocations(queryClient, (all) => all.map((one) => (one.id === id ? record : one)))

  return record
}

export async function queueLocationDelete(queryClient: QueryClient, id: number): Promise<void> {
  const locations = queryClient.getQueryData<Location[]>(queryKeys.locations) ?? []
  const current = locations.find((location) => location.id === id)

  await enqueue({
    id: changesetId(),
    entity: 'location',
    kind: 'delete',
    at: now(),
    target: refFor(id, current?.clientId),
  })

  patchLocations(queryClient, (all) => all.filter((one) => one.id !== id))
}

/**
 * The walking order (issue #27). One whole-order change, and the queue replaces
 * any earlier one rather than stacking — three drags are the third drag, not
 * three round trips.
 */
export async function queueLocationReorder(
  queryClient: QueryClient,
  ids: number[],
): Promise<Location[]> {
  const locations = queryClient.getQueryData<Location[]>(queryKeys.locations) ?? []
  const byId = new Map(locations.map((location) => [location.id, location]))

  await enqueue({
    id: changesetId(),
    entity: 'location',
    kind: 'reorder',
    at: now(),
    order: ids.map((id) => refFor(id, byId.get(id)?.clientId)),
  })

  const ordered = [
    ...ids.map((id) => byId.get(id)).filter((one): one is Location => one !== undefined),
    ...locations.filter((one) => !ids.includes(one.id)),
  ]

  patchLocations(queryClient, () => ordered)

  return ordered
}

// — fixture models —

export async function queueFixtureModelCreate(
  queryClient: QueryClient,
  input: FixtureModelInput,
): Promise<FixtureModel> {
  const clientId = newClientId()
  const fields = carried(input as unknown as Record<string, unknown>)
  const record = provisionalFixtureModel(clientId, fields)

  await enqueue({
    id: changesetId(),
    entity: 'fixtureModel',
    kind: 'create',
    at: now(),
    clientId,
    // Links are not syncable — a spares URL is not what anybody is doing in a
    // cellar, and the server's allowlist would refuse them on arrival.
    fields: withoutLinks(fields),
  })

  patchFixtureModels(queryClient, (models) => [...models, record])

  return record
}

export async function queueFixtureModelUpdate(
  queryClient: QueryClient,
  id: number,
  input: Partial<FixtureModelInput>,
): Promise<FixtureModel> {
  const models = queryClient.getQueryData<FixtureModel[]>(queryKeys.fixtureModels) ?? []
  const current = models.find((model) => model.id === id)

  if (current === undefined) {
    throw new Error('That fixture model is not saved on this device, so it cannot be changed offline.')
  }

  const fields = withoutLinks(carried(input as Record<string, unknown>))
  const record = applyFixtureModelEdit(current, fields)

  await enqueue({
    id: changesetId(),
    entity: 'fixtureModel',
    kind: 'update',
    at: now(),
    target: refFor(id, current.clientId),
    fields: editsFor(fields, (field) =>
      (current as unknown as Record<string, unknown>)[field] ?? null,
    ),
  })

  patchFixtureModels(queryClient, (all) => all.map((one) => (one.id === id ? record : one)))

  return record
}

export async function queueFixtureModelDelete(
  queryClient: QueryClient,
  id: number,
): Promise<void> {
  const models = queryClient.getQueryData<FixtureModel[]>(queryKeys.fixtureModels) ?? []
  const current = models.find((model) => model.id === id)

  await enqueue({
    id: changesetId(),
    entity: 'fixtureModel',
    kind: 'delete',
    at: now(),
    target: refFor(id, current?.clientId),
  })

  patchFixtureModels(queryClient, (all) => all.filter((one) => one.id !== id))
}

function withoutLinks(fields: FieldValues): FieldValues {
  const { links: _links, ...rest } = fields as Record<string, unknown>

  return rest
}

// — stocktakes (issue #64, step 3) —

/**
 * Every item the device has cached.
 *
 * One list since issue #70 — this used to merge a cache entry per filter
 * combination, and a walk of the whole store depending on which filters somebody
 * had happened to visit was exactly the problem that change removed.
 */
function allCachedItems(queryClient: QueryClient): EquipmentItem[] {
  return queryClient.getQueryData<EquipmentItem[]>(queryKeys.items) ?? []
}

function patchStocktakes(
  queryClient: QueryClient,
  update: (list: StocktakeList) => StocktakeList,
): void {
  queryClient.setQueryData<StocktakeList>(queryKeys.stocktakes, (previous) =>
    update(previous ?? { open: null, history: [] }),
  )
}

function summaryOf(detail: StocktakeDetail): StocktakeSummary {
  const { groups: _groups, ...summary } = detail

  return summary
}

export async function queueStocktakeStart(
  queryClient: QueryClient,
  locationIds: number[],
  startedBy: string | null,
): Promise<StocktakeDetail> {
  const clientId = newClientId()
  const locations = queryClient.getQueryData<Location[]>(queryKeys.locations) ?? []
  const byId = new Map(locations.map((location) => [location.id, location]))
  const detail = provisionalStocktake(
    clientId,
    locationIds,
    allCachedItems(queryClient),
    locations,
    startedBy,
  )

  await enqueue({
    id: changesetId(),
    entity: 'stocktake',
    kind: 'create',
    at: now(),
    clientId,
    // Empty means the whole store in walking order, which is what the endpoint
    // means by an empty `locationIds` too.
    locations: locationIds.map((id) => refFor(id, byId.get(id)?.clientId)),
  })

  patchStocktakes(queryClient, (list) => ({ ...list, open: summaryOf(detail) }))
  queryClient.setQueryData(queryKeys.stocktake(detail.id), detail)

  return detail
}

/**
 * Records a count with no connection.
 *
 * The line is named by its **item**, not by its line id: a walk started
 * underground has no line ids to send, and the pairing is unique server-side
 * anyway. `expectedQuantity` rides along because a replay may have to invent
 * the line — and then what the sheet in the walker's hand said is truer than
 * the item's quantity by the time it syncs.
 */
export async function queueLineCount(
  queryClient: QueryClient,
  stocktakeId: number,
  input: {
    itemId: number
    expectedQuantity: number
    countedQuantity?: number | null
    note?: string | null
  },
): Promise<StocktakeLine> {
  const detail = queryClient.getQueryData<StocktakeDetail>(queryKeys.stocktake(stocktakeId))

  if (detail === undefined) {
    throw new Error('That stocktake is not saved on this device, so it cannot be counted offline.')
  }

  const line = detail.groups
    .flatMap((group) => group.lines)
    .find((candidate) => candidate.itemId === input.itemId)

  const { itemId, expectedQuantity, ...changes } = input
  const fields = carried(changes as Record<string, unknown>)
  const next = applyLineCount(detail, itemId, changes)
  const counted = next.groups
    .flatMap((group) => group.lines)
    .find((candidate) => candidate.itemId === itemId)

  if (counted === undefined) {
    throw new Error('That item is not on this stocktake.')
  }

  await enqueue({
    id: changesetId(),
    entity: 'stocktake',
    kind: 'count',
    at: now(),
    target: refFor(stocktakeId, detail.clientId),
    item: { id: itemId },
    expectedQuantity,
    fields: editsFor(fields, (field) =>
      field === 'note' ? (line?.note ?? null) : (line?.countedQuantity ?? null),
    ),
  })

  queryClient.setQueryData(queryKeys.stocktake(stocktakeId), next)
  patchStocktakes(queryClient, (list) => ({ ...list, open: summaryOf(next) }))

  return counted
}

export async function queueStocktakeComplete(
  queryClient: QueryClient,
  stocktakeId: number,
  applyCounts: boolean,
): Promise<StocktakeDetail> {
  return closeWalk(queryClient, stocktakeId, 'complete', applyCounts)
}

export async function queueStocktakeAbandon(
  queryClient: QueryClient,
  stocktakeId: number,
): Promise<StocktakeSummary> {
  return summaryOf(await closeWalk(queryClient, stocktakeId, 'abandon', false))
}

async function closeWalk(
  queryClient: QueryClient,
  stocktakeId: number,
  kind: 'complete' | 'abandon',
  applyCounts: boolean,
): Promise<StocktakeDetail> {
  const detail = queryClient.getQueryData<StocktakeDetail>(queryKeys.stocktake(stocktakeId))

  if (detail === undefined) {
    throw new Error('That stocktake is not saved on this device, so it cannot be finished offline.')
  }

  const target = refFor(stocktakeId, detail.clientId)

  await enqueue(
    kind === 'complete'
      ? { id: changesetId(), entity: 'stocktake', kind, at: now(), target, applyCounts }
      : { id: changesetId(), entity: 'stocktake', kind, at: now(), target },
  )

  const closed: StocktakeDetail = {
    ...detail,
    status: kind === 'complete' ? 'completed' : 'abandoned',
    completedAt: now(),
  }

  queryClient.setQueryData(queryKeys.stocktake(stocktakeId), closed)

  // The walk leaves the "open" slot and joins the history, which is what makes
  // the screen offer to start another one — and what would otherwise leave a
  // walker staring at a finished walk they cannot get out of.
  patchStocktakes(queryClient, (list) => ({
    open: null,
    history: [summaryOf(closed), ...list.history],
  }))

  return closed
}

// — photographs (issue #64, step 4) —

/**
 * Puts a photograph on an item, or takes this item's own one off, with no
 * connection.
 *
 * The bytes are shrunk to what the server keeps and stored under the
 * changeset's own key — see `photoStore.ts` for why both of those, and why the
 * blob is not in the queue's JSON. What the record carries meanwhile is a
 * `queued:` reference, so the walker can see the picture they just took rather
 * than an empty frame and a promise.
 */
export async function queueItemPhoto(
  queryClient: QueryClient,
  id: number,
  file: Blob | null,
): Promise<EquipmentItem> {
  const current = findItem(queryClient, id)

  if (current === null) {
    throw new Error('That item is not saved on this device, so its photograph cannot be changed offline.')
  }

  const image = await queuePhoto('item', refFor(id, current.clientId), file, 'item')
  const record: EquipmentItem = { ...current, image, pending: true }

  patchItemLists(queryClient, (items) => items.map((one) => (one.id === id ? record : one)))
  queryClient.setQueryData(queryKeys.item(id), record)

  return record
}

export async function queueFixtureModelPhoto(
  queryClient: QueryClient,
  id: number,
  file: Blob | null,
): Promise<FixtureModel> {
  const models = queryClient.getQueryData<FixtureModel[]>(queryKeys.fixtureModels) ?? []
  const current = models.find((model) => model.id === id)

  if (current === undefined) {
    throw new Error(
      'That fixture model is not saved on this device, so its photograph cannot be changed offline.',
    )
  }

  const image = await queuePhoto('fixtureModel', refFor(id, current.clientId), file, 'model')
  const record: FixtureModel = { ...current, image, pending: true }

  patchFixtureModels(queryClient, (all) => all.map((one) => (one.id === id ? record : one)))

  return record
}

/**
 * The half both sides share: shrink, store, enqueue, and say what the record's
 * `image` now reads.
 *
 * Clearing answers `null` rather than a reference — which on an item is not
 * quite the whole truth, because a lantern that was overriding its model's
 * catalogue shot drops back to showing that one. The device cannot know what
 * the model holds without asking, so it shows nothing until sync says
 * otherwise, which is the honest way round: claiming a picture is there and
 * being wrong is worse than a gap that fills in.
 */
async function queuePhoto(
  entity: 'item' | 'fixtureModel',
  target: RecordRef,
  file: Blob | null,
  source: RecordImage['source'],
): Promise<RecordImage | null> {
  const { userId, changesets } = useQueueStore.getState()

  if (userId === null) {
    throw new Error('There is no signed-in account to hold this photograph.')
  }

  if (file !== null && countQueuedPhotos(changesets) >= QUEUED_PHOTO_MAX) {
    throw new Error(
      `There are already ${QUEUED_PHOTO_MAX} photographs waiting to be sent. Find a signal before taking more.`,
    )
  }

  const id = changesetId()

  if (file !== null) {
    // Before the changeset is queued, not after: a queued change whose bytes
    // failed to store would be a photograph the walker was told was safe and
    // that nothing could ever send.
    await putQueuedPhoto(userId, id, await shrinkForQueue(file))
  }

  await enqueue({
    id,
    entity,
    kind: 'photograph',
    at: now(),
    target,
    action: file === null ? 'clear' : 'set',
  })

  return file === null ? null : { url: localPhotoUrl(id), source, pending: true }
}

function countQueuedPhotos(changesets: readonly Changeset[]): number {
  return changesets.filter(
    (changeset) => changeset.kind === 'photograph' && changeset.action === 'set',
  ).length
}

/** Named for the tests, which assert what a queue is allowed to be about. */
export const QUEUEABLE_ENTITIES: readonly SyncEntity[] = [
  'item',
  'location',
  'fixtureModel',
  'stocktake',
]
